import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, CheckCircle, XCircle, Clock, MapPin,
  Phone, Mail, Globe, Eye, ChevronDown, ChevronUp, Search,
  Lock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_PASSWORD = 'puppotoadmin2024';

const PupDirectoryAdmin = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  const [submissions, setSubmissions] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  
  // Approval form
  const [approvalData, setApprovalData] = useState({});

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      setAuthError('');
      sessionStorage.setItem('directory_admin', 'true');
    } else {
      setAuthError('Incorrect password');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('directory_admin') === 'true') {
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchSubmissions();
      fetchLocations();
    }
  }, [authenticated, filter]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/submissions?status=${filter}`);
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch (err) {
      toast.error('Error fetching submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/locations`);
      const data = await res.json();
      setLocations(data.locations || {});
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleApprove = async (submissionId) => {
    const data = approvalData[submissionId] || {};
    
    if (!data.state || !data.area) {
      toast.error('Please select state and area before approving');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/submissions/${submissionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          state: data.state,
          area: data.area,
          is_sponsor: data.is_sponsor || false,
          sponsor_tier: data.sponsor_tier || null,
          featured: data.featured || false
        })
      });
      
      const result = await res.json();
      
      if (result.success) {
        toast.success('Business approved and listed!');
        fetchSubmissions();
      } else {
        toast.error(result.detail || 'Error approving submission');
      }
    } catch (err) {
      toast.error('Error approving submission');
    }
  };

  const handleReject = async (submissionId, reason = '') => {
    if (!window.confirm('Are you sure you want to reject this submission?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/puppoto/directory/submissions/${submissionId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      
      const result = await res.json();
      
      if (result.success) {
        toast.success('Submission rejected');
        fetchSubmissions();
      } else {
        toast.error(result.detail || 'Error rejecting submission');
      }
    } catch (err) {
      toast.error('Error rejecting submission');
    }
  };

  const updateApprovalData = (submissionId, field, value) => {
    setApprovalData(prev => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        [field]: value
      }
    }));
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-orange-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Directory Admin</h1>
            <p className="text-gray-500">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 mb-4"
            />
            {authError && (
              <p className="text-red-500 text-sm mb-4 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> {authError}
              </p>
            )}
            <Button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              Access Admin Panel
            </Button>
          </form>
          
          <button 
            onClick={() => navigate('/puppoto/directory')}
            className="w-full mt-4 text-gray-500 text-sm hover:underline"
          >
            Back to Directory
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-6">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/puppoto/directory')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </button>
          <h1 className="text-2xl font-bold">Directory Admin Panel</h1>
          <p className="text-white/70">Review and approve business listings</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected', 'all'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === status 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-yellow-500">
              {submissions.filter(s => s.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-500">Pending</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-green-500">
              {submissions.filter(s => s.status === 'approved').length}
            </div>
            <div className="text-sm text-gray-500">Approved</div>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <div className="text-3xl font-bold text-red-500">
              {submissions.filter(s => s.status === 'rejected').length}
            </div>
            <div className="text-sm text-gray-500">Rejected</div>
          </div>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">No {filter} submissions</h3>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map(sub => (
              <motion.div
                key={sub.submission_id}
                layout
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                {/* Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedId(expandedId === sub.submission_id ? null : sub.submission_id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${
                      sub.status === 'pending' ? 'bg-yellow-400' :
                      sub.status === 'approved' ? 'bg-green-400' : 'bg-red-400'
                    }`} />
                    <div>
                      <h3 className="font-semibold text-gray-800">{sub.business_name}</h3>
                      <p className="text-sm text-gray-500">{sub.category} • {sub.contact_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-400">
                      {new Date(sub.submitted_at).toLocaleDateString()}
                    </span>
                    {expandedId === sub.submission_id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedId === sub.submission_id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-gray-100"
                    >
                      <div className="p-4 grid md:grid-cols-2 gap-6">
                        {/* Business Details */}
                        <div className="space-y-3">
                          <h4 className="font-medium text-gray-700">Business Details</h4>
                          <p className="text-sm text-gray-600">{sub.description || 'No description'}</p>
                          {sub.services?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {sub.services.map((s, i) => (
                                <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">{s}</span>
                              ))}
                            </div>
                          )}
                          <div className="space-y-1 text-sm">
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" /> {sub.email}
                            </p>
                            <p className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" /> {sub.phone}
                            </p>
                            {sub.website && (
                              <p className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-gray-400" />
                                <a href={sub.website} target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">
                                  {sub.website}
                                </a>
                              </p>
                            )}
                            <p className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              {sub.submitted_address?.street}, {sub.submitted_address?.suburb} {sub.submitted_address?.state} {sub.submitted_address?.postcode}
                            </p>
                          </div>
                        </div>
                        
                        {/* Approval Panel */}
                        {sub.status === 'pending' && (
                          <div className="bg-gray-50 rounded-xl p-4">
                            <h4 className="font-medium text-gray-700 mb-3">Approve Listing</h4>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-gray-500">Assign State *</label>
                                <select
                                  value={approvalData[sub.submission_id]?.state || ''}
                                  onChange={(e) => {
                                    updateApprovalData(sub.submission_id, 'state', e.target.value);
                                    updateApprovalData(sub.submission_id, 'area', '');
                                  }}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                >
                                  <option value="">Select state</option>
                                  {Object.entries(locations).map(([code, loc]) => (
                                    <option key={code} value={code}>{loc.name}</option>
                                  ))}
                                </select>
                              </div>
                              
                              <div>
                                <label className="text-xs text-gray-500">Assign Area *</label>
                                <select
                                  value={approvalData[sub.submission_id]?.area || ''}
                                  onChange={(e) => updateApprovalData(sub.submission_id, 'area', e.target.value)}
                                  disabled={!approvalData[sub.submission_id]?.state}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:bg-gray-100"
                                >
                                  <option value="">Select area</option>
                                  {approvalData[sub.submission_id]?.state && 
                                    locations[approvalData[sub.submission_id].state]?.areas.map(area => (
                                      <option key={area} value={area}>{area}</option>
                                    ))
                                  }
                                </select>
                              </div>
                              
                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={approvalData[sub.submission_id]?.is_sponsor || false}
                                    onChange={(e) => updateApprovalData(sub.submission_id, 'is_sponsor', e.target.checked)}
                                    className="rounded"
                                  />
                                  Sponsor
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={approvalData[sub.submission_id]?.featured || false}
                                    onChange={(e) => updateApprovalData(sub.submission_id, 'featured', e.target.checked)}
                                    className="rounded"
                                  />
                                  Featured
                                </label>
                              </div>
                              
                              {approvalData[sub.submission_id]?.is_sponsor && (
                                <div>
                                  <label className="text-xs text-gray-500">Sponsor Tier</label>
                                  <select
                                    value={approvalData[sub.submission_id]?.sponsor_tier || ''}
                                    onChange={(e) => updateApprovalData(sub.submission_id, 'sponsor_tier', e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                  >
                                    <option value="">Select tier</option>
                                    <option value="bronze">Bronze</option>
                                    <option value="silver">Silver</option>
                                    <option value="gold">Gold</option>
                                  </select>
                                </div>
                              )}
                              
                              <div className="flex gap-2 pt-2">
                                <Button
                                  onClick={() => handleApprove(sub.submission_id)}
                                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  onClick={() => handleReject(sub.submission_id)}
                                  variant="outline"
                                  className="flex-1 text-red-500 border-red-200 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Status for processed */}
                        {sub.status !== 'pending' && (
                          <div className={`rounded-xl p-4 ${
                            sub.status === 'approved' ? 'bg-green-50' : 'bg-red-50'
                          }`}>
                            <p className={`font-medium ${
                              sub.status === 'approved' ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {sub.status === 'approved' ? 'Approved' : 'Rejected'}
                            </p>
                            {sub.approved_location && (
                              <p className="text-sm text-gray-600 mt-1">
                                Listed in: {sub.approved_location.area}, {locations[sub.approved_location.state]?.name}
                              </p>
                            )}
                            {sub.rejection_reason && (
                              <p className="text-sm text-gray-600 mt-1">
                                Reason: {sub.rejection_reason}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              Reviewed: {new Date(sub.reviewed_at).toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PupDirectoryAdmin;
